import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "./Motion";
import { Logo } from "@/components/Logo";

const links = [
  { id: "story", label: "Story", href: "#story" },
  { id: "services", label: "Services", href: "#services" },
  { id: "brand-deals", label: "Brand Deals", href: "#brand-deals" },
  { id: "sourcing", label: "Sourcing", href: "#sourcing" },
  { id: "products", label: "Products", href: "#products" },
  { id: "results", label: "Results", href: "#results" },
];

export default function Navbar({ start }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={start ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 1, ease: EASE, delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-[80] transition-colors duration-500 ${
        scrolled && !open ? "border-b border-white/5 bg-ink/80 backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-8 lg:px-16">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <Logo
            className="h-9 w-9 rounded-lg object-cover ring-1 ring-gold/40"
          />
          <span className="text-sm font-bold tracking-[0.22em] text-neutral-100">
            BEATKING<span className="text-gold">SMM</span>
          </span>
          <span className="font-mono text-[10px] text-gold/70">2.0</span>
        </a>

        <nav className="hidden items-center gap-9 lg:flex">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="group relative text-[13px] font-medium tracking-wide text-neutral-400 transition-colors duration-300 hover:text-neutral-100"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/panel/login"
            data-testid="nav-signin"
            className="hidden text-[13px] font-medium text-neutral-400 transition-colors duration-300 hover:text-goldlight sm:block"
          >
            Sign In
          </Link>
          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden rounded-full border border-gold/40 px-5 py-2.5 text-[13px] font-semibold text-goldlight transition-colors duration-300 hover:bg-gold hover:text-black sm:block"
          >
            Partner With Us
          </a>
          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-neutral-200 lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="overflow-hidden border-b border-white/5 bg-ink/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-6 py-6">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="border-b border-white/5 py-3 text-lg font-medium text-neutral-200"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta"
                className="pt-4 font-semibold text-gold"
              >
                Partner With Us
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
